// This React component implements a price range slider using Adobe Spectrum's RangeSlider and Algolia's useRange hook for
// filtering search results. It dynamically adjusts to the available price range and ensures default values for robustness.
// The component maintains the selected range in the state and updates it whenever the available range changes.
// Users can refine their search results by adjusting the slider, which triggers an update through the refine function.
// Additionally, the selected minimum and maximum price values are displayed below the slider.

import { useEffect, useState } from 'react';
import { useRange } from 'react-instantsearch-hooks-web';
import { RangeSlider as SpectrumRangeSlider } from '@adobe/react-spectrum';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';

interface RangeSliderProps {
  attribute: string;
}

export function RangeSlider(props: RangeSliderProps) {
  const { start = [], range, canRefine, refine } = useRange(props);
  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string = localeSelection?.currency || null;

  const isGBP = currency === 'GBP' ? true : false;
  // Ensure min and max have default fallback values
  const min = typeof range.min === 'number' ? range.min : 0;
  const max = typeof range.max === 'number' ? range.max : 100;

  // Handle case where min and max are equal (single price)
  const effectiveMax = max === min ? min + 1 : max;

  // Ensure start array contains valid numbers
  const start0 = Array.isArray(start) && typeof start[0] === 'number' ? start[0] : min;
  const start1 = Array.isArray(start) && typeof start[1] === 'number' ? start[1] : effectiveMax;

  const from = Math.max(min, start0);
  const to = Math.min(effectiveMax, start1);

  const [value, setValue] = useState<{ start: number; end: number }>({
    start: from,
    end: to,
  });

  useEffect(() => {
    setValue({ start: from, end: to });
  }, [from, to]);

  // Convert to the format SpectrumRangeSlider expects
  const sliderValue = {
    start: value?.start,
    end: value?.end,
  };

  return (
    <>
      <SpectrumRangeSlider
        // UNSAFE_className="custom-range-slider"
        // label="Price range"
        minValue={min}
        maxValue={effectiveMax}
        value={sliderValue}
        onChange={(val) => setValue(val)}
        onChangeEnd={(val) => {
          // When min and max are equal in the data, we need to adjust the refined value
          const endValue = max === min ? min : val?.end;
          refine([val?.start, endValue]);
        }}
        isDisabled={!canRefine}
      />

      {/* Min and Max Price Display */}
      <div className="mt-4 flex justify-between text-sm">
        <span>
          {isGBP ? '£' : '€'}
          {(value?.start / 100)?.toFixed(2)}
        </span>
        <span>
          {isGBP ? '£' : '€'}
          {((max === min ? min : value?.end) / 100)?.toFixed(2)}
        </span>
      </div>
    </>
  );
}
